import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { isAuthenticated } from '../../services/cognitoService';
import { getUserProfile } from '../../services/userService';
import { getApiUrl, getAuthHeaders } from '../../utils/api';
import logger from '../../utils/logger';
import './AgeRangeModal.css';

const AGE_RANGES = [
  { value: '', label: 'Select your age range' },
  { value: '13-17', label: '13-17' },
  { value: '18-24', label: '18-24' },
  { value: '25-34', label: '25-34' },
  { value: '35-44', label: '35-44' },
  { value: '45-54', label: '45-54' },
  { value: '55-64', label: '55-64' },
  { value: '65+', label: '65+' },
];

const AgeRangeModal: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const checkAgeRangeNeeded = useCallback(async () => {
    if (!isAuthenticated()) {
      setIsOpen(false);
      return;
    }

    const needsAgeRange = sessionStorage.getItem('theychat-needs-age-range');
    if (needsAgeRange === 'true') {
      setIsOpen(true);
      return;
    }

    // Check backend profile if we haven't checked during this session yet
    const profileChecked = sessionStorage.getItem('theychat-age-range-checked');
    if (!profileChecked) {
      try {
        const profile = await getUserProfile();
        sessionStorage.setItem('theychat-age-range-checked', 'true');
        if (!profile?.age_range) {
          sessionStorage.setItem('theychat-needs-age-range', 'true');
          setIsOpen(true);
        } else {
          sessionStorage.removeItem('theychat-needs-age-range');
          window.dispatchEvent(new CustomEvent('age-range-completed'));
        }
      } catch (err) {
        logger.warn('AgeRangeModal', 'Failed to check profile for age range', err as Error);
      }
    }
  }, []);

  // Re-check on route changes so the flag set by AuthCallback or profile state is detected immediately after redirect
  useEffect(() => {
    checkAgeRangeNeeded();
  }, [location.pathname, checkAgeRangeNeeded]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        // Don't allow closing without selecting - age range is required
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!selectedRange) {
      setError(t('ageRangeModal.selectRequired', 'Please select your age range'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const apiUrl = getApiUrl();
      const response = await fetch(`${apiUrl}/user/profile`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ age_range: selectedRange }),
      });

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }

      sessionStorage.removeItem('theychat-needs-age-range');
      sessionStorage.setItem('theychat-age-range-checked', 'true');
      setIsOpen(false);
      window.dispatchEvent(new CustomEvent('age-range-completed'));
    } catch (_err) {
      setError(t('ageRangeModal.updateError', 'Failed to save. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="age-range-modal-overlay">
      <div
        className="age-range-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="age-range-modal-title"
      >
        {/* Icon */}
        <div className="age-range-modal-icon">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <title>User Icon</title>
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        </div>

        {/* Title */}
        <h2 id="age-range-modal-title" className="age-range-modal-title">
          {t('ageRangeModal.title', 'Complete Your Profile')}
        </h2>

        {/* Description */}
        <p className="age-range-modal-desc">
          {t('ageRangeModal.description', 'Please select your age range to continue.')}
        </p>

        {/* Error */}
        {error && <p className="age-range-modal-error">{error}</p>}

        {/* Dropdown */}
        <div className="age-range-modal-select-wrapper">
          <select
            value={selectedRange}
            onChange={(e) => {
              setSelectedRange(e.target.value);
              if (error) setError('');
            }}
            className="age-range-modal-select"
            disabled={loading}
          >
            {AGE_RANGES.map((range) => (
              <option key={range.value || 'default'} value={range.value}>
                {range.value
                  ? range.label
                  : t('ageRangeModal.placeholder', 'Select your age range')}
              </option>
            ))}
          </select>
          <svg
            className="age-range-modal-select-arrow"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <title>Select Arrow</title>
            <path d="M6 9l6 6 6-6" />
          </svg>
        </div>

        {/* Submit button */}
        <button
          type="button"
          className="age-range-modal-btn"
          onClick={handleSubmit}
          disabled={loading || !selectedRange}
        >
          {loading ? t('common.saving', 'Saving...') : t('ageRangeModal.submit', 'Continue')}
        </button>
      </div>
    </div>
  );
};

export default AgeRangeModal;
